// src/components/partner/BalanceCard.tsx

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { AddCircleOutline, Wallet } from '@mui/icons-material';

import { toUsdDisplay } from '@utils/money';

type SectionWrapperArgs = {
  title: string;
  icon?: React.ReactNode;
  right?: React.ReactNode;
  description?: string;
  highlighted?: boolean;
  children: React.ReactNode;
};

type TxType = 'DEPOSIT' | 'WITHDRAW';

type Props = {
  balance: number;
  deposit: number;
  setDeposit: (value: number) => void;
  onApplyDeposit: () => Promise<void> | void;
  applying?: boolean;
  sectionWrapper: (args: SectionWrapperArgs) => React.ReactElement;
};

const INPUT_HEIGHT_SM = 40;

const BalanceCard = ({
  balance,
  deposit,
  setDeposit,
  onApplyDeposit,
  applying = false,
  sectionWrapper,
}: Props) => {
  const [txType, setTxType] = useState<TxType>('DEPOSIT');
  const [amountInput, setAmountInput] = useState<string>('');

  // Reset input when parent clears deposit (e.g. after apply).
  useEffect(() => {
    if (deposit === 0) setAmountInput('');
  }, [deposit]);

  const parseAmount = useCallback((raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return { nextInput: '', nextValue: 0 };
    // Positive numbers only — no leading zeros except "0." forms.
    if (!/^(?!0\d)\d*\.?\d*$/.test(trimmed)) return null;
    const n = Number(trimmed);
    if (!Number.isFinite(n) || n < 0) return null;
    return { nextInput: trimmed, nextValue: n };
  }, []);

  const handleAmountChange = useCallback(
    (raw: string) => {
      const parsed = parseAmount(raw);
      if (!parsed) return;
      setAmountInput(parsed.nextInput);
      setDeposit(txType === 'WITHDRAW' ? -parsed.nextValue : parsed.nextValue);
    },
    [parseAmount, setDeposit, txType],
  );

  const handleTypeChange = useCallback(
    (type: TxType) => {
      setTxType(type);
      const amount = Number(amountInput) || 0;
      setDeposit(type === 'WITHDRAW' ? -amount : amount);
    },
    [amountInput, setDeposit],
  );

  const canApply = !applying && deposit !== 0;

  return sectionWrapper({
    title: 'Balance',
    icon: <Wallet fontSize="small" />,
    children: (
      <Stack spacing={2} sx={{ minWidth: 0 }}>
        <TextField
          label="Current Balance"
          value={toUsdDisplay(balance)}
          size="small"
          fullWidth
          disabled
        />

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ minWidth: 0 }}>
          {/* Type selector + amount input */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <TextField
              label="Amount"
              value={amountInput}
              size="small"
              type="text"
              fullWidth
              placeholder="0"
              disabled={applying}
              onChange={(e) => handleAmountChange(e.target.value)}
              InputProps={{
                inputMode: 'decimal',
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 0 }}>
                    <Select
                      value={txType}
                      onChange={(e) => handleTypeChange(e.target.value as TxType)}
                      size="small"
                      disabled={applying}
                      variant="standard"
                      disableUnderline
                      sx={{
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: txType === 'WITHDRAW' ? 'error.main' : 'success.main',
                        minWidth: 90,
                        '& .MuiSelect-icon': { fontSize: '1rem' },
                      }}
                    >
                      <MenuItem value="DEPOSIT">Deposit</MenuItem>
                      <MenuItem value="WITHDRAW">Withdraw</MenuItem>
                    </Select>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Apply button */}
          <Box
            sx={{
              flexShrink: 0,
              minWidth: { xs: '100%', md: 160 },
              display: 'flex',
              alignItems: 'flex-start',
            }}
          >
            <Button
              variant="contained"
              size="small"
              fullWidth
              startIcon={
                applying ? <CircularProgress size={16} color="inherit" /> : <AddCircleOutline />
              }
              onClick={onApplyDeposit}
              disabled={!canApply}
              sx={{ whiteSpace: 'nowrap', height: INPUT_HEIGHT_SM }}
            >
              {applying ? 'Applying…' : txType === 'WITHDRAW' ? 'Apply Withdrawal' : 'Apply Deposit'}
            </Button>
          </Box>
        </Stack>
      </Stack>
    ),
  });
};

export default BalanceCard;
